import type { CSSProperties } from "react";
import { motion } from "motion/react";
import { bouncy, cn } from "../ui";

const fills = {
  blue: "bg-primary",
  orange: "bg-accent",
  tint: "bg-primary-tint",
  cream: "bg-cream",
};

/** The H mark's bar: upright sides, ends sloped up to the right. `flip` slopes them down instead. */
const bar = "polygon(0 17%, 100% 0, 100% 83%, 0 100%)";
const barFlip = "polygon(0 0, 100% 17%, 100% 100%, 0 83%)";

export type BarProps = {
  tone?: keyof typeof fills;
  flip?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Seconds before it snaps in. Bars without a delay render still. */
  delay?: number;
  /** Snap in when scrolled into view instead of on mount. */
  inView?: boolean;
  opacity?: number;
};

/** A slanted plane cut from the logo's geometry, for decoration behind sections. */
export function Bar({ tone = "blue", flip, className, style, delay, inView, opacity = 1 }: BarProps) {
  const clip = { clipPath: flip ? barFlip : bar, opacity, ...style };
  if (delay === undefined) return <span aria-hidden className={cn("pointer-events-none absolute block", fills[tone], className)} style={clip} />;
  const from = { opacity: 0, scaleY: 0.3, skewY: flip ? 14 : -14 };
  const to = { opacity, scaleY: 1, skewY: 0, transition: { ...bouncy, delay } };
  return (
    <motion.span
      aria-hidden
      className={cn("pointer-events-none absolute block origin-bottom", fills[tone], className)}
      style={clip}
      initial={from}
      {...(inView ? { whileInView: to, viewport: { once: true } } : { animate: to })}
    />
  );
}
